import { VFXEffectConfig } from '../../VFXEffectRegistry';

type Primitive = VFXEffectConfig['primitives'][number];

// =============================================================================
// Status-aura primitive builders — the recurring grammar of persistent auras
// =============================================================================
//
// Extracted from StatusVFX.ts + namedEffectGenerator.buildStatusAuraEffect
// (2026-07-17) after both grew the same four shapes independently:
//   pulsingShell — the breathing body-envelope every aura starts from
//   orbiter      — a mote/shard/star circling the target
//   driftMotes   — scattered motes rising (regen, heat) or dripping (poison)
//   groundRing   — the squashed ellipse at the feet (slow, regen)
//
// Same philosophy as the atmosphere presetBuilders: fold the scaffolding,
// keep per-status FLAVOR at the call site (a builder call reads as one line
// of intent; a status keeps its bespoke one-offs — poison's bubble pop,
// frozen's shard ring — as literals). Output-preserving on extraction:
// locked by the StatusVFX snapshot + the generator unit tests.

export interface PulseSpec {
  /** Peak scale of the breathe (omit to pulse alpha only). */
  scale?: number;
  /** Low-water alpha of the breathe (omit to pulse scale only). */
  alpha?: number;
  duration: number;
}

/** The breathing envelope around the body — every status aura's base layer. */
export function pulsingShell(opts: {
  width: number; height: number; color: number; alpha: number;
  stroke?: { color: number; width: number };
  glow?: boolean; offsetY?: number; depth?: number;
  pulse: PulseSpec;
}): Primitive {
  const p: Primitive = {
    type: 'ellipse', width: opts.width, height: opts.height,
    color: opts.color, alpha: opts.alpha, depth: opts.depth ?? 50,
    tween: {
      ...(opts.pulse.scale !== undefined ? { scale: opts.pulse.scale } : {}),
      ...(opts.pulse.alpha !== undefined ? { alpha: opts.pulse.alpha } : {}),
      duration: opts.pulse.duration, ease: 'Sine.easeInOut', yoyo: true, repeat: -1,
    },
  };
  if (opts.glow) p.glow = true;
  if (opts.stroke) p.stroke = opts.stroke;
  if (opts.offsetY !== undefined) p.offsetY = opts.offsetY;
  return p;
}

/** A mote/shard/star circling the target. Flicker adds a looping alpha/scale
 *  shimmer on top of the orbit. */
export function orbiter(opts: {
  shape?: 'circle' | 'rect' | 'star';
  radius?: number; width?: number; height?: number;
  color: number; alpha: number; depth?: number; glow?: boolean; offsetY?: number;
  orbit: { radius: number; speed: number; startAngle?: number; orientToPath?: boolean };
  flicker?: { alpha?: number; scale?: number; duration: number };
}): Primitive {
  const p: Primitive = {
    type: opts.shape ?? 'circle',
    color: opts.color, alpha: opts.alpha, depth: opts.depth ?? 52,
    orbit: opts.orbit,
  };
  if (opts.radius !== undefined) p.radius = opts.radius;
  if (opts.width !== undefined) p.width = opts.width;
  if (opts.height !== undefined) p.height = opts.height;
  if (opts.glow) p.glow = true;
  if (opts.offsetY !== undefined) p.offsetY = opts.offsetY;
  if (opts.flicker) {
    p.tween = {
      ...(opts.flicker.alpha !== undefined ? { alpha: opts.flicker.alpha } : {}),
      ...(opts.flicker.scale !== undefined ? { scale: opts.flicker.scale } : {}),
      duration: opts.flicker.duration, yoyo: true, repeat: -1,
    };
  }
  return p;
}

/** Scattered motes drifting off the target — 'up' for life/heat, 'down' for
 *  drips. `travel` is the unsigned distance; direction supplies the sign. */
export function driftMotes(opts: {
  direction: 'up' | 'down';
  count: number; spread: number; radius: number;
  color: number; alpha: number; depth?: number; glow?: boolean;
  travel: number; duration: number; delay?: number;
  stroke?: { color: number; width: number };
}): Primitive {
  const p: Primitive = {
    type: 'circle', radius: opts.radius,
    color: opts.color, alpha: opts.alpha, depth: opts.depth ?? 52,
    scatter: { count: opts.count, radius: opts.spread },
    tween: {
      y: opts.direction === 'up' ? -opts.travel : opts.travel,
      alpha: 0, duration: opts.duration, repeat: -1,
      ...(opts.delay !== undefined ? { delay: opts.delay } : {}),
    },
  };
  if (opts.glow) p.glow = true;
  if (opts.stroke) p.stroke = opts.stroke;
  return p;
}

/** The squashed ellipse at the feet (drag puddles, ground glows). */
export function groundRing(opts: {
  width: number; height: number; color: number; alpha: number;
  offsetY?: number; depth?: number; glow?: boolean;
  stroke?: { color: number; width: number };
  pulse: PulseSpec;
}): Primitive {
  return pulsingShell({ ...opts, offsetY: opts.offsetY ?? 26, depth: opts.depth ?? 49 });
}

/** N items evenly spaced on one orbit — stun's star carousel, curse of the
 *  elements' multi-hue element ring. Items inherit the shared orbit and are
 *  phase-offset automatically; per-item color/size/alpha keeps mixed-palette
 *  carousels honest (each element keeps its own hue). */
export function carousel(opts: {
  items: Array<{ color: number; radius?: number; width?: number; height?: number; alpha?: number }>;
  shape?: 'circle' | 'rect' | 'star';
  orbitRadius: number; speed: number;
  offsetY?: number; depth?: number; glow?: boolean;
  flicker?: { alpha?: number; scale?: number; duration: number };
}): Primitive[] {
  const step = (Math.PI * 2) / opts.items.length;
  return opts.items.map((item, i) => orbiter({
    shape: opts.shape,
    radius: item.radius, width: item.width, height: item.height,
    color: item.color, alpha: item.alpha ?? 0.9,
    depth: opts.depth, glow: opts.glow, offsetY: opts.offsetY,
    orbit: { radius: opts.orbitRadius, speed: opts.speed, ...(i > 0 ? { startAngle: step * i } : {}) },
    flicker: opts.flicker,
  }));
}
